import React, { useEffect, useState } from "react";
import { ButtonVariant, Checkbox, Dialog, Link, Text } from "@merge-api/merge-javascript-shared";
import { pascalCaseToTitleCase } from "../../../../../../utils/casing";
import { ScopeOption } from "../../../../../../models/CommonModel";
import { MERGE_COMMON_MODEL_SCOPES_HELP_CENTER } from "../../../../../../router/RouterUtils";
import { DependentScopeGroup } from "../utils/fetchDependentScopeGroups";

interface Props {
  open: boolean;
  saving: boolean;
  /** Dependent-model groups to confirm before re-syncing; empty skips straight to the re-sync step. */
  dependentGroups: DependentScopeGroup[];
  onConfirm: () => void;
  onClose: () => void;
}

const dedupe = (models: string[]) => [...new Set(models)];

type Step = "off" | "on" | "resync";

/**
 * Confirms saving scope edits as a sequence of up to three steps inside one Dialog (same title,
 * buttons, AND height throughout, only the body swaps): (1) read access turned OFF for cascaded
 * models, gated by a confirmation checkbox; (2) read access turned ON for cascaded models; (3) the
 * re-sync notice. Steps with nothing to show are skipped, so with no dependents the dialog opens
 * straight on re-sync. The body is a fixed height so the dialog doesn't resize between steps; the
 * model list scrolls within its own fixed-height box. The backend applies the cascade at save.
 */
const SaveScopesDialog = ({ open, saving, dependentGroups, onConfirm, onClose }: Props) => {
  // state
  const [stepIndex, setStepIndex] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [resyncConfirmed, setResyncConfirmed] = useState(false);

  // derived state
  const offModels = dedupe(
    dependentGroups
      .filter((group) => group.scope === ScopeOption.DISABLED)
      .flatMap((g) => g.models),
  );
  const onModels = dedupe(
    dependentGroups
      .filter((group) => group.scope !== ScopeOption.DISABLED)
      .flatMap((g) => g.models),
  );
  // Ordered, applicable-only steps: off (if any) -> on (if any) -> re-sync (always last).
  const steps: Step[] = [];
  if (offModels.length) steps.push("off");
  if (onModels.length) steps.push("on");
  steps.push("resync");

  const step = steps[stepIndex] ?? "resync";
  const isLastStep = stepIndex >= steps.length - 1;
  // The off step can't be advanced until the user acknowledges losing read access, and the re-sync
  // step can't be saved until they acknowledge the full re-sync.
  const primaryDisabled = (step === "off" && !confirmed) || (step === "resync" && !resyncConfirmed);

  // effects
  // Restart at the first step and clear the confirmation each time the dialog reopens.
  useEffect(() => {
    if (open) {
      setStepIndex(0);
      setConfirmed(false);
      setResyncConfirmed(false);
    }
  }, [open]);

  // render
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Editing Scopes will re-sync all Linked Accounts"
      variant="md"
      primaryButtonText={isLastStep ? "Save and re-sync data" : "Continue"}
      primaryButtonVariant={ButtonVariant.PrimaryGray}
      primaryButtonLoading={isLastStep && saving}
      primaryButtonDisabled={primaryDisabled}
      onPrimaryButtonClick={isLastStep ? onConfirm : () => setStepIndex((i) => i + 1)}
      secondaryButtonText="Cancel"
      secondaryButtonVariant={ButtonVariant.TextBlack}
      onSecondaryButtonClick={onClose}
    >
      {/* With dependent steps, a fixed height keeps the dialog from resizing as you step through
          them; with only the re-sync step (no dependents) there are no prior screens to match, so it
          sizes to its natural height. The model list fills the space (flex-1) and scrolls in its box. */}
      <div className={steps.length > 1 ? "h-72" : undefined}>
        {step === "off" && (
          <div className="flex h-full flex-col gap-4">
            <Text>
              These changes will result in read access being turned off for the following related
              models:
            </Text>
            <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-gray-10 px-4 py-3">
              <ul className="ml-4 list-disc">
                {offModels.map((model) => (
                  <li key={model}>
                    <Text>{pascalCaseToTitleCase(model)}</Text>
                  </li>
                ))}
              </ul>
            </div>
            <Checkbox checked={confirmed} onChange={setConfirmed} label="I understand" />
          </div>
        )}
        {step === "on" && (
          <div className="flex h-full flex-col gap-4">
            <Text>
              These changes will result in read access being turned on for the following related
              models:
            </Text>
            <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-gray-10 px-4 py-3">
              <ul className="ml-4 list-disc">
                {onModels.map((model) => (
                  <li key={model}>
                    <Text>{pascalCaseToTitleCase(model)}</Text>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {step === "resync" && (
          <div className="flex h-full flex-col gap-4">
            <Text>
              Saving these changes initiates a full re-sync across all of your Linked Accounts, not
              only the ones being edited. Depending on data volume, this process may take several
              hours to several days and cannot be interrupted once it has begun.
            </Text>
            <Text>
              Your existing data will remain available throughout. Newly enabled fields will appear
              only after the re-sync has completed in full.{" "}
              <Link href={MERGE_COMMON_MODEL_SCOPES_HELP_CENTER} target="_blank">
                Learn more.
              </Link>
            </Text>
            <Checkbox
              checked={resyncConfirmed}
              onChange={setResyncConfirmed}
              labelAlignment="start"
              label="I understand this will re-sync all my Linked Accounts"
            />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default SaveScopesDialog;
